import React from 'react';
import { TransferConversationDialog } from '@/shared/components/dialogs/TransferConversationDialog';
import { useKanbanDensity } from '@/shared/components/kanban/KanbanDensityContext';
import { useConversationCardBehavior } from '../hooks/useConversationCardBehavior';
import { buildConversationCardFacts } from './cards/facts';
import { ConversationCardCompact } from './cards/ConversationCardCompact';
import { ConversationCardDetailed } from './cards/ConversationCardDetailed';
import { ConversationCardNormal } from './cards/ConversationCardNormal';
import type { ConversationCardViewProps } from './cards/types';
import type { AttendanceAction, AttendanceCardConversation } from '../types';

export type { AttendanceCardConversation };

export interface ConversationCardImprovedProps {
  conversation: AttendanceCardConversation;
  onAction?: (action: AttendanceAction, conversation: AttendanceCardConversation) => void;
  /** @deprecated aceita e ignorada — ver Tarefa 17. */
  showOperator?: boolean;
  providerId?: string;
}

/**
 * Card de conversa do Kanban de atendimentos.
 *
 * Contêiner: resolve comportamento e leitura uma vez e escolhe a aparência pela
 * densidade do board. As três não são o mesmo card com padding diferente —
 * são três desenhos com hierarquias próprias, escolhidos numa galeria de 60:
 *   detailed → Atendimento 20 "Seções com divisor"
 *   normal   → Atendimento 01 "Cabeçalho de pessoa"
 *   compact  → Atendimento 06 "Barra de esfriamento" (padrão do módulo)
 *
 * Fora de um board a densidade cai em 'normal' (padrão do contexto), então o
 * card continua renderizável em qualquer outro lugar.
 */
export const ConversationCardImproved: React.FC<ConversationCardImprovedProps> = ({
  conversation,
  onAction,
  providerId,
}) => {
  const density = useKanbanDensity();
  const behavior = useConversationCardBehavior({ conversation, onAction });
  const facts = buildConversationCardFacts(conversation, {
    currentUserId: behavior.currentUserId,
    providerId,
  });

  const view: ConversationCardViewProps = {
    facts,
    actions: behavior.actions,
    statusTag: behavior.statusTag,
    onCardClick: behavior.onCardClick,
  };

  return (
    <>
      {density === 'detailed' && <ConversationCardDetailed {...view} />}
      {density === 'normal' && <ConversationCardNormal {...view} />}
      {density === 'compact' && <ConversationCardCompact {...view} />}

      {behavior.transferDialog.isOpen && (
        <TransferConversationDialog
          conversationId={facts.id}
          conversationSubject={facts.contactName}
          isOpen
          onClose={behavior.transferDialog.close}
          onTransfer={behavior.transferDialog.submit}
          isLoading={behavior.transferDialog.isLoading}
        />
      )}
    </>
  );
};
